import type { User } from '@auth0/auth0-react';

/** Shape expected by MaHeaderUserMenu (mirrors MA `userInfo`). */
export type MaHeaderUserInfo = {
  firstName?: string;
  lastName?: string;
  email?: string;
  avatar?: string | null;
};

/**
 * Port of Master Account `getUserDisplayName` — prefers profile fields, then Auth0 claims.
 */
export function getMaHeaderDisplayName(
  userInfo: MaHeaderUserInfo | null | undefined,
  authUser: User | undefined
): string {
  const first = (userInfo?.firstName || '').trim();
  const last = (userInfo?.lastName || '').trim();
  const combined = `${first} ${last}`.trim();
  if (combined) return combined;

  const auth = authUser;
  if (auth) {
    const gn = (auth.given_name || '').trim();
    const fn = (auth.family_name || '').trim();
    const fromClaims = `${gn} ${fn}`.trim();
    if (fromClaims) return fromClaims;
    if (typeof auth.name === 'string' && auth.name.trim()) return auth.name.trim();
  }

  if (userInfo?.email) return String(userInfo.email).trim();
  if (auth?.email) return String(auth.email).trim();
  return 'User';
}

/**
 * Port of Master Account `getUserInitials` — prefers profile names, then Auth0, then email.
 */
export function getMaHeaderInitials(
  userInfo: MaHeaderUserInfo | null | undefined,
  authUser: User | undefined
): string {
  const first = (userInfo?.firstName || '').trim();
  const last = (userInfo?.lastName || '').trim();
  if (first && last) {
    return (first.charAt(0) + last.charAt(0)).toUpperCase();
  }
  if (first) {
    return first.length >= 2 ? first.slice(0, 2).toUpperCase() : first.charAt(0).toUpperCase();
  }

  const auth = authUser;
  if (auth) {
    const gn = (auth.given_name || '').trim();
    const fn = (auth.family_name || '').trim();
    if (gn && fn) return (gn.charAt(0) + fn.charAt(0)).toUpperCase();
    if (gn) return gn.length >= 2 ? gn.slice(0, 2).toUpperCase() : gn.charAt(0).toUpperCase();
    if (typeof auth.name === 'string' && auth.name.trim()) {
      const parts = auth.name.trim().split(/\s+/).filter(Boolean);
      if (parts.length >= 2) {
        return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
      }
      if (parts[0]?.length >= 2) return parts[0].slice(0, 2).toUpperCase();
      if (parts[0]) return parts[0].charAt(0).toUpperCase();
    }
  }

  const email = (userInfo?.email || auth?.email || '').trim();
  if (email) {
    const local = email.split('@')[0] || email;
    return local.slice(0, 2).toUpperCase();
  }
  return 'U';
}
